import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import { BrandingProvider } from "./context/BrandingContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ROUTES, ADMIN_SEGMENTS } from "./routes/paths";
import "./styles/global.css";

const Home = lazy(() => import("./pages/Home"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const Dashboard = lazy(() => import("./pages/dashboard/Dashboard"));
const HealthTracker = lazy(() => import("./pages/health/HealthTracker"));
const PregnancyTracker = lazy(() => import("./pages/pregnancy/PregnancyTracker"));
const PeriodTracker = lazy(() => import("./pages/period/PeriodTracker"));
const BabyCare = lazy(() => import("./pages/baby/BabyCare"));
const AppointmentList = lazy(() => import("./pages/appointment/AppointmentList"));
const Reports = lazy(() => import("./pages/reports/Reports"));
const Articles = lazy(() => import("./pages/content/Articles"));
const ArticleDetail = lazy(() => import("./pages/content/ArticleDetail"));
const WellnessSpace = lazy(() => import("./pages/wellness/WellnessSpace"));
const Tools = lazy(() => import("./pages/tools/Tools"));
const BabyNames = lazy(() => import("./pages/babynames/BabyNames"));
const EssentialStore = lazy(() => import("./pages/store/EssentialStore"));
const HealthDetailList = lazy(() => import("./pages/health/HealthDetailList"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminAppointments = lazy(() => import("./pages/admin/AdminAppointments"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminPregnancyWeeks = lazy(() => import("./pages/admin/AdminPregnancyWeeks"));
const AdminBranding = lazy(() => import("./pages/admin/AdminBranding"));
const AdminSubscription = lazy(() => import("./pages/admin/AdminSubscription"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const AdminBabyNames = lazy(() => import("./pages/admin/AdminBabyNames"));
const AdminBabyNameAttributes = lazy(() => import("./pages/admin/AdminBabyNameAttributes"));
const AdminStore = lazy(() => import("./pages/admin/AdminStore"));
const Subscription = lazy(() => import("./pages/Subscription"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const AccountDeletion = lazy(() => import("./pages/legal/AccountDeletion"));
const NotFound = lazy(() => import("./pages/NotFound"));

function App() {
  return (
    <BrandingProvider>
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true
      }}
    >
      <ThemeProvider>
      <Suspense
        fallback={
          <div className="mkc-app-loading" role="status" aria-live="polite">
            Loading…
          </div>
        }
      >
        <Routes>
          <Route element={<Layout />}>
            <Route path={ROUTES.HOME} element={<Home />} />
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.REGISTER} element={<Register />} />
            <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
            <Route path={ROUTES.TERMS} element={<TermsOfService />} />
            <Route path={ROUTES.ACCOUNT_DELETION} element={<AccountDeletion />} />
            <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
            <Route path={ROUTES.HEALTH} element={<HealthTracker />} />
            <Route path="health/:domain" element={<HealthDetailList />} />
            <Route path={ROUTES.PREGNANCY} element={<PregnancyTracker />} />
            <Route path={ROUTES.PERIOD} element={<PeriodTracker />} />
            <Route path={ROUTES.BABY} element={<BabyCare />} />
            <Route path={ROUTES.APPOINTMENTS} element={<AppointmentList />} />
            <Route path={ROUTES.REPORTS} element={<Reports />} />
            <Route path="/articles/:id" element={<ArticleDetail />} />
            <Route path={ROUTES.ARTICLES} element={<Articles />} />
            <Route path={ROUTES.WELLNESS} element={<WellnessSpace />} />
            <Route path={ROUTES.TOOLS} element={<Tools />} />
            <Route path={ROUTES.BABY_NAMES} element={<BabyNames />} />
            <Route path={ROUTES.STORE} element={<EssentialStore />} />
            <Route path={ROUTES.PROFILE} element={<Profile />} />
            <Route path={ROUTES.SUBSCRIPTION} element={<Subscription />} />
            <Route path="admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path={ADMIN_SEGMENTS.USERS} element={<AdminUsers />} />
              <Route path={ADMIN_SEGMENTS.CONTENT} element={<AdminContent />} />
              <Route path={ADMIN_SEGMENTS.APPOINTMENTS} element={<AdminAppointments />} />
              <Route path={ADMIN_SEGMENTS.REPORTS} element={<AdminReports />} />
              <Route path={ADMIN_SEGMENTS.PREGNANCY_WEEKS} element={<AdminPregnancyWeeks />} />
              <Route path={ADMIN_SEGMENTS.BRANDING} element={<AdminBranding />} />
              <Route path={ADMIN_SEGMENTS.SUBSCRIPTION} element={<AdminSubscription />} />
              <Route path={ADMIN_SEGMENTS.PAYMENTS} element={<AdminPayments />} />
              <Route path={ADMIN_SEGMENTS.BABY_NAMES} element={<AdminBabyNames />} />
              <Route path={ADMIN_SEGMENTS.BABY_NAME_ATTRIBUTES} element={<AdminBabyNameAttributes />} />
              <Route path={ADMIN_SEGMENTS.STORE} element={<AdminStore />} />
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </ThemeProvider>
    </Router>
    </BrandingProvider>
  );
}

export default App;
